import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { dump as toYaml } from 'js-yaml';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgData } from '../../hooks/useOrgData';

const today = new Date().toISOString().split('T')[0];
const thisYear = new Date().getFullYear();

const PRESETS = [
  { label: 'This year',  from: `${thisYear}-01-01`,     to: today },
  { label: 'Last year',  from: `${thisYear-1}-01-01`,   to: `${thisYear-1}-12-31` },
  { label: '2 years',    from: `${thisYear-2}-01-01`,   to: `${thisYear-2}-12-31` },
  { label: 'All time',   from: '2000-01-01',             to: today },
];

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ExportTransactions = () => {
  const orgId = useOrgId();
  const { accounts, funds } = useOrgData(orgId);
  const activeAccounts = accounts.filter(a => a.active !== false).map(a => a.name);
  const activeFunds    = funds.filter(f => f.active !== false).map(f => f.name);

  const [fromDate, setFromDate]       = useState(`${thisYear}-01-01`);
  const [toDate, setToDate]           = useState(today);
  const [filterAccount, setFilterAccount] = useState('');
  const [filterFund, setFilterFund]   = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [exported, setExported]       = useState(false);

  useEffect(() => {
    if (!fromDate || !toDate || !orgId) return;
    setLoading(true);
    setExported(false);
    const q = query(
      collection(db, 'orgs', orgId, 'transactions'),
      where('date', '>=', fromDate),
      where('date', '<=', toDate),
      orderBy('date', 'asc'),
    );
    getDocs(q).then(snap => {
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [orgId, fromDate, toDate]);

  const filtered = transactions.filter(t =>
    (!filterAccount || t.account === filterAccount) &&
    (!filterFund    || t.classification === filterFund)
  );

  const nonStarting   = filtered.filter(t => t.type !== 'Starting Balance');
  const totalIn       = nonStarting.reduce((s, t) => s + (t.income  || 0), 0);
  const totalOut      = nonStarting.reduce((s, t) => s + (t.expense || 0), 0);

  const applyPreset = (p) => { setFromDate(p.from); setToDate(p.to); };

  const doExport = (format) => {
    const rows = filtered.map(({ id, createdAt, createdBy, ...tx }) => tx);

    let content, mime, ext;
    if (format === 'yaml') {
      content = toYaml({ from: fromDate, to: toDate, transactions: rows }, { lineWidth: 120 });
      mime = 'text/yaml';
      ext  = 'yaml';
    } else {
      // CSV
      const cols = ['date','account','classification','type','income','expense','description','payableTo','donor','checkNum','cleared','split'];
      const escape = (v) => {
        const s = v == null ? '' : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [cols.join(','), ...rows.map(r => cols.map(c => escape(r[c])).join(','))];
      content = lines.join('\n');
      mime = 'text/csv';
      ext  = 'csv';
    }

    const suffix = [
      filterAccount ? `-${filterAccount}` : '',
      filterFund    ? `-${filterFund}`    : '',
    ].join('');

    const blob = new Blob([content], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `transactions-${fromDate}-to-${toDate}${suffix}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
    setExported(true);
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Export Transactions</h2>
        <p className="text-slate-500 text-sm mt-0.5">Filter then download as CSV or YAML</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 space-y-5">

        {/* Date range */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Date Range</label>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date" value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
            />
            <span className="text-slate-500 text-sm">to</span>
            <input
              type="date" value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
            />
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            {PRESETS.map(p => (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-colors
                  ${fromDate === p.from && toDate === p.to
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-200 text-slate-600 hover:bg-slate-50'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* Account filter */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Account</label>
          <div className="flex gap-2 flex-wrap">
            {['', ...activeAccounts].map(a => (
              <button
                key={a || '__all__'}
                onClick={() => setFilterAccount(a)}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-colors
                  ${filterAccount === a
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-200 text-slate-600 hover:bg-slate-50'}`}
              >
                {a || 'All accounts'}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* Fund filter */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fund</label>
          <div className="flex gap-2 flex-wrap">
            {['', ...activeFunds].map(f => (
              <button
                key={f || '__all__'}
                onClick={() => setFilterFund(f)}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-colors
                  ${filterFund === f
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-200 text-slate-600 hover:bg-slate-50'}`}
              >
                {f || 'All funds'}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* Summary */}
        <div className="bg-slate-50 rounded-lg px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          {loading ? (
            <span className="text-slate-500 text-sm">Loading…</span>
          ) : (
            <>
              <span className="text-slate-700 text-sm font-medium">
                {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
              </span>
              <span className="text-xs text-slate-500">
                <span className="text-green-600 font-medium">{fmt(totalIn)} in</span>
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="text-red-500 font-medium">{fmt(totalOut)} out</span>
              </span>
            </>
          )}
        </div>

        {/* Export buttons */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={() => doExport('csv')}
            disabled={loading || filtered.length === 0}
            className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Download CSV
          </button>
          <button
            onClick={() => doExport('yaml')}
            disabled={loading || filtered.length === 0}
            className="flex-1 border border-gray-200 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Download YAML
          </button>
        </div>

        {exported && (
          <p className="text-xs text-green-600 text-center">File downloaded successfully.</p>
        )}
      </div>
    </div>
  );
};

export default ExportTransactions;
